/**
 * Connect RPC error-code helpers, shared between the query client's retry
 * policy (main.tsx) and any page that needs to distinguish "you don't have
 * access" from a generic failure (SUN-408 Phase 6 addendum) — checked by
 * numeric value, not instanceof, to avoid cross-package identity issues
 * with ConnectError.
 */

const CONNECT_CODE_PERMISSION_DENIED = 7;
const CONNECT_CODE_NOT_FOUND = 5;
const CONNECT_CODE_FAILED_PRECONDITION = 9;
const CONNECT_CODE_UNAUTHENTICATED = 16;

function connectErrorCode(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) return undefined;
  const code = (error as { code: unknown }).code;
  return typeof code === 'number' ? code : undefined;
}

/** True if error is a Connect RPC PermissionDenied — the caller's role/access_rules don't grant this action, as opposed to a NotFound or a transient failure. */
export function isPermissionDeniedError(error: unknown): boolean {
  return connectErrorCode(error) === CONNECT_CODE_PERMISSION_DENIED;
}

/** True if error is a Connect RPC FailedPrecondition — e.g. CreateInvitation/
 * ResendInvitation's ErrInvitationEmailFailed, where the DB row exists but
 * the Auth0 email call failed. Distinct from a generic failure because the
 * account really was created/updated; the caller should be told that. */
export function isFailedPreconditionError(error: unknown): boolean {
  return connectErrorCode(error) === CONNECT_CODE_FAILED_PRECONDITION;
}

// Connect RPC codes that are definitive failures — no point retrying.
const NO_RETRY_CONNECT_CODES = new Set([
  CONNECT_CODE_NOT_FOUND,
  CONNECT_CODE_PERMISSION_DENIED,
  CONNECT_CODE_UNAUTHENTICATED,
]);

export function isDefinitiveConnectError(error: unknown): boolean {
  const code = connectErrorCode(error);
  return code !== undefined && NO_RETRY_CONNECT_CODES.has(code);
}

/** Message to show a caller who just got denied — distinct from a generic
 * failure, since "you don't have permission" and "something went wrong"
 * call for different next actions (ask an admin vs. try again). */
export function errorToastMessage(error: unknown, fallback: string): string {
  if (isPermissionDeniedError(error)) {
    return "You don't have permission to do this.";
  }
  return (error as { message?: string })?.message ?? fallback;
}
